import { lazy, Suspense, useEffect, useId, useRef, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { PW, type TransferPlaceFieldProps } from "@majourneys/booking";

/**
 * The Google Maps picker lives in its own chunk. `app-root.tsx` mounts the
 * booking provider on every page, so importing `place-field.tsx` there would
 * put `@vis.gl/react-google-maps` on the first load of pages that have no
 * transfer form at all. This file is the only thing app-root imports: the
 * chunk is requested when a transfer form first renders a place field.
 */
const LazyPlaceField = lazy(() => import("./place-field"));

/**
 * Read here rather than through `@majourneys/discover`, whose barrel carries
 * the Discover map components. Same variable `brand-map.ts` reads.
 */
const MAPS_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";

/** What the field shows until the chunk arrives: the same label and box, inert. */
function PlaceFieldPlaceholder({ kind, label, invalid, autoFocus }: TransferPlaceFieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  // Holds the focus the real field takes over when it arrives (see `Search`).
  useEffect(() => {
    if (autoFocus) ref.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on arrival only
  }, []);
  return (
    <div>
      <label htmlFor={id} className="mb-1 block font-sans text-[12px]" style={{ color: PW.muted }}>
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        data-place-placeholder=""
        type="text"
        readOnly
        className="mj-pw-field h-11 w-full px-3 text-start text-[16px] outline-none"
        style={{ borderRadius: 14, color: PW.navy }}
        placeholder={t(`transport.search_${kind}`)}
        aria-invalid={invalid || undefined}
      />
    </div>
  );
}

/**
 * Client-only: the server and the first client render both paint the
 * placeholder, so hydration never meets a suspended lazy component, and the
 * Maps chunk is never pulled into the SSR render.
 */
/**
 * Set once the first field has mounted on this page load. Only the field in
 * the server's HTML has to start as the placeholder; one that appears later
 * (the restaurant ride's pickup, under its switch) goes straight to the real
 * search. Starting every field as the placeholder swapped one input for
 * another a frame after it appeared, which threw away the focus the switch
 * had just put there (owner, 2026-10-12).
 */
let hydrated = false;

export function TransportPlaceField(props: TransferPlaceFieldProps) {
  const [mounted, setMounted] = useState(hydrated);
  useEffect(() => {
    hydrated = true;
    setMounted(true);
  }, []);
  if (!mounted) return <PlaceFieldPlaceholder {...props} />;
  return (
    <Suspense fallback={<PlaceFieldPlaceholder {...props} />}>
      <LazyPlaceField {...props} />
    </Suspense>
  );
}

/**
 * The field to hand `BookingProvider`, or nothing without a Maps key. With
 * nothing, the transfer form shows its WhatsApp link instead of a picker that
 * could never produce coordinates.
 */
export function transportPlaceFieldFor(apiKey: string): ComponentType<TransferPlaceFieldProps> | undefined {
  return apiKey ? TransportPlaceField : undefined;
}

export const TRANSPORT_PLACE_FIELD = transportPlaceFieldFor(MAPS_KEY);
